import React, { useState, useEffect } from 'react';
import './biomedical-engineering-and-sports-biomechanics-advisory.css';

function BiomedicalEngineeringAndSportsBiomechanicsAdvisory() {
  const [activeSection, setActiveSection] = useState('about');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setActiveSection(sectionId);
    setIsMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['about', 'services', 'specialized', 'ai-integration', 'rehabilitation', 'team', 'value'];
      const scrollPosition = window.scrollY + 200;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetBottom = offsetTop + element.offsetHeight;
          if (scrollPosition >= offsetTop && scrollPosition < offsetBottom) {
            setActiveSection(section);
            break;
          }
        }
      }
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const coreServices = [
    { icon: "", title: "Biomechanical Movement Analysis", desc: "Advanced assessment of joint motion, alignment, movement efficiency, and force distribution.", outcome: "Identification of performance limitations and injury risks." },
    { icon: "", title: "Injury Prevention & Risk Profiling", desc: "Biomechanical risk assessment, load and stress analysis, preventive strategy design.", outcome: "Reduced injuries and improved athlete longevity." },
    { icon: "", title: "Sports Technique Optimization", desc: "Sport-specific movement correction, technical refinement, efficiency enhancement.", outcome: "Improved performance with reduced energy waste." },
    { icon: "", title: "Performance Optimization Programs", desc: "Personalized plans integrating physical metrics, biomechanical data, and psychological factors.", outcome: "Maximum performance with sustainable progression." },
    { icon: "", title: "Strength & Conditioning Advisory", desc: "Functional strength planning, injury-safe training guidance, performance conditioning strategies.", outcome: "Stronger, more resilient athletes." },
    { icon: "", title: "Sports Nutrition Advisory", desc: "Customized nutrition plans, recovery-focused diet strategies, performance-based intake optimization.", outcome: "Improved endurance, recovery, and performance output." },
    { icon: "", title: "Sports Psychology Integration", desc: "Mental resilience training, performance focus development, stress and pressure management.", outcome: "Stronger mindset and competitive edge." }
  ];

  const specializedFocus = [
    { title: "Cricket Bowling Biomechanics", features: ["Bowling action analysis", "Spine and shoulder load management", "Speed and accuracy optimization"], flagship: true },
    { title: "Running & Sprint Mechanics", features: ["Stride efficiency", "Ground contact optimization", "Speed enhancement strategies"], flagship: false },
    { title: "Joint Load & Injury Reduction", features: ["Stress distribution correction", "Movement redesign", "Long-term joint protection"], flagship: false },
    { title: "Injury Risk Mapping", features: ["Data-driven injury prediction", "Athlete-specific risk profiling"], flagship: false },
    { title: "Recovery Optimization", features: ["Fatigue management", "Recovery protocols", "Return-to-play guidance"], flagship: false }
  ];

  const teamMembers = [
    { icon: "", title: "Medical Doctors", desc: "With biomedical engineering expertise", hasImage: false },
    { icon: "", title: "Biomedical Engineers", desc: "Hospital-experienced professionals", hasImage: false },
    { icon: "", title: "Registered Physiotherapists", desc: "Clinical rehabilitation specialists", hasImage: false },
    { icon: "", title: "Sports Science Consultants", desc: "Performance optimization experts", hasImage: false },
    { icon: "", title: "Performance Analysts", desc: "Data-driven insights", hasImage: false },
    { icon: "", title: "Registered Dietitian", desc: "Sports nutrition expertise", hasImage: false },
    { icon: "", title: "Experienced Sports Practitioners", desc: "Practical application specialists", hasImage: false }
  ];

  return (
    <div className="sb-master-container">
      {/* Hero Banner Section */}
  
 <section className="sb-hero-section">

  {/* BACKGROUND IMAGE */}
  <img
    src="/sport-banner.jpeg"
    alt="Sports Banner"
    className="sb-hero-bg"
  />

  <div className="sb-hero-overlay"></div>
  <div className="sb-hero-pattern"></div>

  <div className="sb-hero-content">
    <div className="sb-hero-badge">DR. JERY MEDICO (PVT) LTD</div>

    <h1 className="sb-hero-title">
      Biomedical Engineering &<br />
      <span className="sb-hero-highlight">
        Sports Biomechanics Advisory Division
      </span>
    </h1>

    <p className="sb-hero-subtitle">Engineering Human Performance</p>

    <div className="sb-hero-quote">
      <p>Scientific performance optimization for athletes and institutions</p>
    </div>

    <button className="sb-hero-cta" onClick={() => scrollToSection('about')}>
      Explore Our Services
      <span className="sb-cta-arrow">→</span>
    </button>
  </div>

  <div className="sb-hero-scroll">
    <div className="sb-scroll-mouse"></div>
  </div>

</section>

     

      {/* About Us Section */}
<section id="about" className="sb-section sb-about-section">

  <div className="sb-container">

    <div className="sb-section-header">
      <span className="sb-section-tag">About Us</span>
      <h2 className="sb-section-title">
        Next-Generation Performance Optimization
      </h2>
      <div className="sb-title-underline"></div>
    </div>

    {/* NEW LAYOUT */}
    <div className="sb-about-layout">


      {/* RIGHT CONTENT */}
      <div className="sb-about-content">

        <p className="sb-lead-text">
          The Sports & Exercise Biomechanics Advisory Division of Dr. Jery Medico (Pvt) Ltd is a next-generation, multidisciplinary performance optimization platform integrating:
        </p>

        <div className="sb-integration-grid">
          <span className="sb-integration-tag">Medical Science</span>
          <span className="sb-integration-plus">+</span>
          <span className="sb-integration-tag">Biomedical Engineering</span>
          <span className="sb-integration-plus">+</span>
          <span className="sb-integration-tag">Sports Science</span>
          <span className="sb-integration-plus">+</span>
          <span className="sb-integration-tag">Applied Psychology</span>
          <span className="sb-integration-plus">+</span>
          <span className="sb-integration-tag">AI & Data Analytics</span>
        </div>

        <div className="sb-disclaimer-box">
          <p>⚠️ We are not a gym. We are not a coaching academy.</p>
          <p className="sb-disclaimer-text">
            We are a high-level scientific advisory center dedicated to analyzing, optimizing, and protecting human athletic performance through evidence-based methodologies and advanced analytical frameworks.
          </p>
        </div>

      </div>


            {/* LEFT IMAGE */}
      <div className="sb-about-image">
        <img
          src="/biomedical-eng.jpeg"
          alt="Biomedical Engineering"
        />
      </div>


    </div>

  </div>

</section>

      {/* Vision & Mission Section */}
     <section className="sb-section sb-vision-section">

  <div className="sb-container">

    <div className="sb-vision-layout">

   

      {/* RIGHT CONTENT */}
      <div className="sb-vision-grid">

        <div className="sb-vision-card">
          <div className="sb-vision-icon">👁️</div>
          <h3>Our Vision</h3>
          <p>
            To become Sri Lanka's leading sports performance engineering and biomechanics advisory center, setting new standards in:
          </p>
          <ul>
            <li>Athlete performance optimization</li>
            <li>Injury prevention</li>
            <li>Data-driven sports science</li>
          </ul>
        </div>

        <div className="sb-mission-card">
          <div className="sb-mission-icon">🎯</div>
          <h3>Our Mission</h3>
          <p>
            To empower athletes, coaches, and institutions by delivering:
          </p>
          <ul>
            <li>Scientifically validated insights</li>
            <li>Personalized performance strategies</li>
            <li>AI-driven analytics</li>
            <li>Ethical, multidisciplinary guidance</li>
          </ul>
        </div>

      </div>

         {/* LEFT IMAGE */}
      <div className="sb-vision-image">
        <img
          src="/nutrition-sports-biomachanics.jpeg"
          alt="Sports Biomechanics Nutrition"
        />
      </div>

    </div>

  </div>

</section>

      {/* Who We Serve Section */}
      <section className="sb-section sb-audience-section">
        <div className="sb-container">
          <div className="sb-section-header">
            <span className="sb-section-tag">Who We Serve</span>
            <h2 className="sb-section-title">Trusted by Athletes & Institutions</h2>
            <div className="sb-title-underline"></div>
          </div>
          
          <div className="sb-audience-grid">
            <div className="sb-audience-card">
              <div className="sb-audience-icon"></div>
              <h3>Athletes</h3>
              <ul>
                <li>School athletes (U15, U17, U19)</li>
                <li>Amateur and recreational players</li>
                <li>Professional and elite athletes</li>
              </ul>
            </div>
            <div className="sb-audience-card">
              <div className="sb-audience-icon"></div>
              <h3>Sports Disciplines</h3>
              <div className="sb-sports-tags">
                <span>Cricket</span>
                <span>Football</span>
                <span>Rugby</span>
                <span>Volleyball</span>
                <span>Basketball</span>
                <span>Athletics</span>
              </div>
            </div>
            <div className="sb-audience-card">
              <div className="sb-audience-icon"></div>
              <h3>Institutions</h3>
              <ul>
                <li>Schools & Sports academies</li>
                <li>Clubs and training centers</li>
                <li>Coaching professionals</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Core Services Section */}
      <section id="services" className="sb-section sb-services-section">
        <div className="sb-container">
          <div className="sb-section-header">
            <span className="sb-section-tag">What We Offer</span>
            <h2 className="sb-section-title">Our Core Services</h2>
            <div className="sb-title-underline"></div>
          </div>

          <div className="sb-services-grid">
            {coreServices.map((service, idx) => (
              <div key={idx} className="sb-service-card">
                <div className="sb-service-icon">{service.icon}</div>
                <h3 className="sb-service-title">{service.title}</h3>
                <p className="sb-service-desc">{service.desc}</p>
                <div className="sb-service-outcome">
                  <strong>Outcome:</strong> {service.outcome}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>



<section className="mid-photo-area">
  <div className="mid-photo-grid">
  <img src="/biomedical-engineering2.jpg" alt="Biomedical 2" />
   <img src="/biomedical-engineering4.jpg" alt="Biomedical 4" />
  


  </div>
</section>



      {/* Specialized Focus Areas */}
      <section id="specialized" className="sb-section sb-specialized-section">
        <div className="sb-container">
          <div className="sb-section-header">
            <span className="sb-section-tag sb-section-tag-light">Specialized Focus</span>
            <h2 className="sb-section-title sb-title-light">Flagship & Specialized Services</h2>
            <div className="sb-title-underline sb-underline-light"></div>
          </div>

          <div className="sb-specialized-grid">
            {specializedFocus.map((item, idx) => (
              <div key={idx} className={`sb-specialized-card ${item.flagship ? 'sb-flagship' : ''}`}>
                {item.flagship && <div className="sb-flagship-badge">Flagship Service</div>}
                <h3>{item.title}</h3>
                <ul>
                  {item.features.map((feature, i) => <li key={i}>{feature}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

<section className="mid-photo-area">
  <div className="mid-photo-grid">

  <img src="/biomedical-engineering1.jpeg" alt="Biomedical 1" />
    <img src="/biomedical-engineering3.jpg" alt="Biomedical 3" />
   

  </div>
</section>

      {/* AI & Data Science Integration */}
      <section id="ai-integration" className="sb-section sb-ai-section">
        <div className="sb-container">
          <div className="sb-section-header">
            <span className="sb-section-tag">Innovation</span>
            <h2 className="sb-section-title">AI & Data Science Integration</h2>
            <div className="sb-title-underline"></div>
            <p className="sb-section-subtitle">Bringing cutting-edge technology into sports performance</p>
          </div>

          <div className="sb-ai-grid">
            <div className="sb-ai-card">
              
              <h3>AI-Based Movement Analysis</h3>
              <ul>
                <li>Automated motion tracking</li>
                <li>Pattern recognition</li>
                <li>Predictive injury analytics</li>
              </ul>
            </div>

            <div className="sb-ai-card">
              <div className="sb-ai-icon"></div>
              <h3>Performance Data Reports</h3>
              <p>For schools, academies, and clubs:</p>
              <ul>
                <li>Athlete performance dashboards</li>
                <li>Injury risk analytics</li>
                <li>Comparative performance metrics</li>
              </ul>
            </div>

            <div className="sb-ai-card">
              <div className="sb-ai-icon"></div>
              <h3>Institutional Analytics</h3>
              <ul>
                <li>Team performance benchmarking</li>
                <li>Training system optimization</li>
                <li>Talent identification frameworks</li>
              </ul>
            </div>
          </div>

          <div className="sb-integrated-modules">
            <h3>Integrated Performance Modules</h3>
            <div className="sb-modules-grid">
              <div className="sb-module">
                <h4>Sports Applied Psychology</h4>
                <p>Mental conditioning programs, focus and discipline training, emotional resilience building</p>
              </div>
              <div className="sb-module">
                <h4>Exercise Physiology Guidance</h4>
                <p>Energy system optimization, endurance and strength balance, fatigue management</p>
              </div>
              <div className="sb-module">
                <h4>Nutrition Framework</h4>
                <p>Age-specific dietary guidance, sport-specific nutrition planning, recovery-based intake strategies</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rehabilitation Support Pathway */}
      <section id="rehabilitation" className="sb-section sb-rehab-section">
        <div className="sb-container">
          <div className="sb-section-header">
            <span className="sb-section-tag sb-section-tag-light">Recovery</span>
            <h2 className="sb-section-title sb-title-light">Rehabilitation Support Pathway</h2>
            <div className="sb-title-underline sb-underline-light"></div>
          </div>

          <div className="sb-rehab-content">
            <p>We provide structured rehabilitation guidance through:</p>
            <div className="sb-rehab-team">
              <span>Registered Physiotherapists</span>
              <span>Injury-Specific Recovery Plans</span>
              <span>Functional Return-to-Play Protocols</span>
            </div>
            <div className="sb-rehab-assurance">
              <p>Ensuring:</p>
              <ul>
                <li>Clinical Safety</li>
                <li>Ethical Compliance</li>
                <li>Evidence-Based Recovery</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Our Expert Team */}
      <section id="team" className="sb-section sb-team-section">
        <div className="sb-container">
          <div className="sb-section-header">
            <span className="sb-section-tag">Experts</span>
            <h2 className="sb-section-title">Our Expert Team</h2>
            <div className="sb-title-underline"></div>
          </div>

          <div className="sb-team-grid">
            {teamMembers.map((member, idx) => (
              <div key={idx} className="sb-team-card">
                {member.hasImage ? (
                  <img src="/profile.png" alt={member.title} className="sb-team-img" />
                ) : (
                  <div className="sb-team-icon">{member.icon}</div>
                )}
                <h4>{member.title}</h4>
                <p>{member.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section id="value" className="sb-section sb-value-section">
        <div className="sb-container">
          <div className="sb-section-header">
            <span className="sb-section-tag">Why Choose Us</span>
            <h2 className="sb-section-title">Our Value Proposition</h2>
            <div className="sb-title-underline"></div>
            <p className="sb-section-subtitle">What Makes Us Different?</p>
          </div>

          <div className="sb-value-grid">
            <div className="sb-value-card">
              <div className="sb-value-icon"></div>
              <h3>Medical + Engineering Integration</h3>
            </div>
            <div className="sb-value-card">
              <div className="sb-value-icon"></div>
              <h3>Data-Driven Performance Analysis</h3>
            </div>
            <div className="sb-value-card">
              <div className="sb-value-icon"></div>
              <h3>AI-Powered Insights</h3>
            </div>
            <div className="sb-value-card">
              <div className="sb-value-icon"></div>
              <h3>Multidisciplinary Expertise</h3>
            </div>
            <div className="sb-value-card">
              <div className="sb-value-icon"></div>
              <h3>Ethical Advisory Approach</h3>
            </div>
          </div>

          <div className="sb-outcome-section">
            <h3>Outcome-Driven Approach</h3>
            <div className="sb-outcome-grid">
              <div className="sb-outcome-box">
                <h4>For Athletes</h4>
                <ul>
                  <li>Enhanced performance</li>
                  <li>Reduced injury rates</li>
                  <li>Extended athletic career</li>
                  <li>Improved efficiency</li>
                </ul>
              </div>
              <div className="sb-outcome-box">
                <h4>For Institutions</h4>
                <ul>
                  <li>Better athlete management</li>
                  <li>Data-driven coaching</li>
                  <li>Reduced injury costs</li>
                  <li>Improved team performance</li>
                </ul>
              </div>
              <div className="sb-outcome-box">
                <h4>For Medical Integration</h4>
                <ul>
                  <li>Better injury understanding</li>
                  <li>Improved communication</li>
                  <li>Evidence-based decisions</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="sb-philosophy-box">
            <p className="sb-philosophy-text">"Every movement can be measured. Every inefficiency can be corrected. Every athlete can be optimized."</p>
          </div>

          <div className="sb-future-vision">
            <h3>Future Vision</h3>
            <div className="sb-vision-grid">
              <span>AI-powered athlete mobile apps</span>
              <span>Wearable device integration</span>
              <span>International consulting services</span>
              <span>Research & innovation</span>
              <span>Strategic sports partnerships</span>
            </div>
          </div>

          <div className="sb-business-model">
            <h3>Business Model Positioning</h3>
            <p>Premium advisory and analytical service provider offering:</p>
            <div className="sb-model-grid">
              <span>Individual Consultations</span>
              <span>Institutional Partnerships</span>
              <span>Performance Audits</span>
              <span>Long-term Advisory</span>
            </div>
          </div>
        </div>
      </section>


    </div>
  );
}

export default BiomedicalEngineeringAndSportsBiomechanicsAdvisory;